import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import { AISystemsService } from 'services/AISystemService';
import { DatasetsService } from 'services';
import { Toast, Utils } from 'shared/helpers';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import DebouncedSearch from 'shared/components/DebouncedSearch';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';

export interface Question {
  id: string;
  text: string;
  type: string;
  options?: any[];
}
export interface Subsection {
  title: string;
  questions: Question[];
}
export interface Section {
  title: string;
  subsections: Subsection[];
}
export interface ITemplate {
  id: string;
  templateName: string;
  description: string;
  scope: string | 'AI' | 'Both' | 'Dataset';
  sections: Section[];
  created_at: string;
  is_active: boolean;
  version: string;
  company_id: any;
  category?: string;
  tags?: string[];
  questionCount?: number;
  estMinutes?: number;
  covers?: string[];
  noOfSections: number;
  noOfSubsections: number;
  noOfQuestions: number;
  estimateToComplete: number;
}

interface CategoryStyle {
  color: string;
  bg: string;
  icon: React.ReactElement;
}

const FALLBACK_STYLE: CategoryStyle = {
  color: '#06362B',
  bg: '#eef0f4',
  icon: <DescriptionOutlinedIcon />,
};

const getCategoryStyle = (): CategoryStyle => FALLBACK_STYLE;

const scopeLabel = (scope: string): string => {
  if (scope === 'AI') return 'AI system';
  if (scope === 'Dataset') return 'Dataset';
  if (scope === 'Both') return 'AI system & Dataset';
  return scope;
};
const STEPS = ['Choose template', 'Select items', 'Review & confirm'];

const TemplateStep: React.FC<{
  templates: ITemplate[];
  selected?: ITemplate;
  onSelect: (t: ITemplate) => void;
}> = ({ templates, selected, onSelect }) => {
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? templates.filter((t) =>
          [t.templateName, t.category, t.description, ...(t.tags ?? [])]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : templates;

    const map = new Map<string, ITemplate[]>();
    filtered.forEach((t) => {
      const cat = t.category || 'General';
      map.set(cat, [...(map.get(cat) ?? []), t]);
    });
    return Array.from(map.entries());
  }, [templates, query]);

  const selectedStyle = getCategoryStyle();

  return (
    <Stack
      direction="row"
      className="app-box-border"
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          pt: 2,
          pl: 2,
        }}
      >
        <TextField
          size="small"
          placeholder={`Search ${templates.length} templates by name, regulation, or risk area...`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            mr: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: 14,
              bgcolor: '#fff',
            },
          }}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            mt: 1,
            pr: 2,
            pb: 1.5,
          }}
        >
          {groups.length === 0 && (
            <Typography sx={{ p: 3, textAlign: 'center', fontSize: 14 }}>
              No templates match “{query}”.
            </Typography>
          )}

          {groups.map(([category, items]) => {
            const style = getCategoryStyle();
            return (
              <Box key={category} sx={{ mt: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: style.color,
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: style.color,
                    }}
                  >
                    {category} · {items.length}
                  </Typography>
                </Stack>

                <Stack>
                  {items.map((t) => {
                    const isSelected = selected?.id === t.id;

                    return (
                      <Box
                        key={t.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={isSelected}
                        onClick={() => onSelect(t)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelect(t);
                          }
                        }}
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          boxSizing: 'border-box',
                          p: 1.5,
                          mb: 1,
                          cursor: 'pointer',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          borderRadius: 2,
                          borderColor: isSelected ? style.color : 'transparent',
                          bgcolor: isSelected ? style.bg : 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: style.bg,
                          },
                          '&:focus-visible': {
                            outline: '2px solid',
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ width: '100%', minWidth: 0 }}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              border: `1px solid ${
                                isSelected ? style.color : '#d6d6d6'
                              }`,
                              borderRadius: 2,
                              bgcolor: isSelected ? style.color : '#fff',
                              color: isSelected ? '#fff' : style.color,
                              display: 'grid',
                              placeItems: 'center',
                              flex: 'none',
                              '& svg': { fontSize: 18 },
                            }}
                          >
                            {style.icon}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: 'text.primary',
                              }}
                              noWrap
                            >
                              {t.templateName}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.5,
                                fontSize: 12,
                                color: 'text.secondary',
                              }}
                            >
                              {scopeLabel(t.scope)}
                              {t.questionCount != null &&
                                ` · ${t.questionCount} Qs`}
                            </Typography>
                          </Box>

                          {isSelected && (
                            <CheckCircleIcon
                              sx={{
                                color: style.color,
                                fontSize: 22,
                                flex: 'none',
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          width: 340,
          flex: 'none',
          overflowY: 'auto',
          borderLeft: '1px solid #e3e0e0',
        }}
      >
        {!selected ? (
          <Stack
            alignItems="center"
            spacing={2}
            sx={{ height: '100%', minHeight: 300, p: 5, textAlign: 'center' }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#eceef3',
                display: 'grid',
                placeItems: 'center',
                '& svg': { fontSize: 22 },
              }}
            >
              <DescriptionOutlinedIcon />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
              Select a template
            </Typography>
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.5 }}>
              Pick an assessment template from the list to see what it covers.
            </Typography>
          </Stack>
        ) : (
          <Box>
            <Box sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    bgcolor: selectedStyle.color,
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    '& svg': { fontSize: 22 },
                  }}
                >
                  {selectedStyle.icon}
                </Box>

                <Chip size="small" label={`${selected.category} Selected`} />
              </Stack>
              <Typography
                sx={{
                  mt: 1.75,
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                {selected.templateName}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2.5,
                bgcolor: '#fff',
                borderBottom: `1px solid #d7d5d5`,
              }}
            >
              {[
                { label: 'Applicability', value: scopeLabel(selected.scope) },
                selected.sections != null && {
                  label: 'Sections',
                  value: selected.sections.length,
                },
                selected.questionCount != null && {
                  label: 'Questions',
                  value: selected.questionCount,
                },
                selected.estMinutes != null && {
                  label: 'Est. time',
                  value: `${selected.estMinutes} min`,
                },
                selected.version != null && {
                  label: 'Version',
                  value: selected.version,
                },
              ]
                .filter(Boolean)
                .map((row: any) => (
                  <Stack
                    key={row.label}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 0.75 }}
                  >
                    <Typography variant="body1">{row.label}</Typography>
                    <Typography variant="body1">{row.value}</Typography>
                  </Stack>
                ))}
            </Box>

            {(selected.covers?.length ?? 0) > 0 && (
              <Box sx={{ px: 2.5, pt: 2, pb: 0.5, bgcolor: '#fff' }}>
                <Typography
                  sx={{
                    mb: 1.25,
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  What you’ll answer
                </Typography>
                <Stack spacing={0.5}>
                  {selected.covers!.map((c) => (
                    <Stack
                      key={c}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ py: 0.5 }}
                    >
                      <CheckIcon
                        sx={{ fontSize: 14, color: selectedStyle.color }}
                      />
                      <Typography sx={{ fontSize: 13 }}>{c}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {selected.description && (
              <Typography
                sx={{
                  p: '12px 20px 20px',
                  bgcolor: '#fff',
                  fontSize: 13,
                  lineHeight: 1.55,
                  // color: MUTED,
                }}
              >
                {selected.description}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

const AssessmentEntity: React.FC<any> = (props) => {
  const { entityTitle, items, defaultIds, onChange, onSearch } = props;
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultIds);

  const handleToggle = (item: any): void => {
    const idx = selectedIds.indexOf(item.id);
    const next = [...selectedIds];
    if (idx === -1) next.push(item.id);
    else next.splice(idx, 1);
    setSelectedIds(next);
    onChange(next);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">{entityTitle}</Typography>
        <Chip size="small" label={`${selectedIds.length} Selected`} />
      </Stack>

      <Box sx={{ mt: 2 }}>
        <DebouncedSearch
          label={`Search ${entityTitle}`}
          placeholder={`Search ${entityTitle}`}
          delay={500}
          onDebounce={(value) => onSearch(value)}
          size="small"
        />
      </Box>

      <List sx={{ maxHeight: 385, minHeight: 385, overflowY: 'auto' }}>
        {items.map((item: any) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton onClick={() => handleToggle(item)}>
              <ListItemIcon>
                <Checkbox
                  color="primary"
                  edge="start"
                  checked={selectedIds.includes(item.id)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography color="text.primary">{item.title}</Typography>
                }
                secondary={item.description}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

interface RunRiskAssessmentProps {
  open: boolean;
  onCloseDialog: (value: boolean) => void;
}

const RunRiskAssessment: React.FC<RunRiskAssessmentProps> = ({
  open,
  onCloseDialog,
}) => {
  const aISystemPagination = useSelector(
    (s: IAppState) => s.paginationState.aISystem
  );
  const datasetPagination = useSelector(
    (s: IAppState) => s.paginationState.dataset
  );

  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate | null>();
  const [selectedAISystems, setSelectedAISystems] = useState<string[]>([]);
  const [selectedDataSets, setSelectedDataSets] = useState<string[]>([]);
  const [creatingAssessment, setCreatingAssessment] = useState(false);
  const [aiSystems, setAiSystems] = useState<any[]>([]);
  const [datasets, setDatasets] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const navigate = useNavigate();

  const loadAISystems = (search: string): void => {
    const qs = new URLSearchParams({
      offset: '0',
      limit: '1000',
      solutionName: search,
    }).toString();
    AISystemsService.getAISystems(qs).then((res) => {
      const { aiSystems } = res.data.data;
      setAiSystems(
        aiSystems.map((a: any) => ({
          ...a,
          id: a._id,
          title: a.solutionName,
          description: `${a.riskTier} `,
        }))
      );
    });
  };

  const loadDatasets = (search: string): void => {
    const qs = new URLSearchParams({
      offset: '0',
      limit: '1000',
      name: search,
    }).toString();
    DatasetsService.getDatasets(qs).then((res) => {
      const { datasets } = res.data.data;
      setDatasets(
        datasets.map((a: any) => ({
          ...a,
          id: a._id,
          title: a.datasetName,
          description: `${a.riskTier} `,
        }))
      );
    });
  };

  const loadAssessmentTemplates = (): void => {
    setLoadingTemplates(true);
    RiskAssessmentService.getAssessmentTemplates()
      .then((res) => {
        const { data } = res.data;
        setTemplates(
          data?.templates.map((d: any) => ({
            ...d,
            id: d._id,
            questionCount: d.noOfQuestions,
            estMinutes: d.estimateToComplete,
            tags: d.complianceMapping ?? [],
            sectionCount: d.noOfSections,
          }))
        );
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => setLoadingTemplates(false));
  };

  const createAssessment = (assessments: any[], callback: any): void => {
    const promises = assessments.map((a) =>
      RiskAssessmentService.submitRiskAssessment(a)
    );
    setCreatingAssessment(true);
    Promise.all(promises)
      .then((responses) => callback(responses))
      .catch((error) => {
        console.log(error, 'sss');
        Toast.showInfoMessage('Something went wront');
      })
      .finally(() => setCreatingAssessment(false));
  };

  const canMoveNext = useMemo(() => {
    if (step === 0) return selectedTemplate;
    if (step === 1)
      return selectedAISystems.length + selectedDataSets.length > 0;
    return true;
  }, [selectedTemplate, step, selectedAISystems, selectedDataSets]);

  const actionOnClickPrevious = (): void => setStep((p) => p - 1);

  const actionOnClickNext = (): void => {
    if (step === 2) {
      const assessments: any[] = [];
      selectedDataSets.forEach((s) =>
        assessments.push({
          templateId: selectedTemplate?.id,
          datasetId: s,
          status: 'In Progress',
          assessmentType: 'self',
        })
      );
      selectedAISystems.forEach((s) =>
        assessments.push({
          templateId: selectedTemplate?.id,
          aiInventoryId: s,
          status: 'In Progress',
          assessmentType: 'self',
        })
      );
      createAssessment(assessments, (responses: any[]) => {
        navigate(`/risk-assessments/${responses[0].data.data._id}/continue`, {
          state: {
            assessment: {
              datasets: datasets.filter((d) => selectedDataSets.includes(d.id)),
              aiSystems: aiSystems.filter((d) =>
                selectedAISystems.includes(d.id)
              ),
              assessmentIds: responses.map((r: any) => r.data.data),
              template: selectedTemplate,
              userResponses: {},
            },
          },
        });
      });
    } else {
      setStep((p) => p + 1);
    }
  };

  useEffect(() => {
    loadAssessmentTemplates();
    loadDatasets('');
    loadAISystems('');
  }, [aISystemPagination.search, datasetPagination.search]);

  const isLast = step === STEPS.length - 1;
  const getNextLabel = (): string => {
    if (creatingAssessment) return 'Processing';
    if (isLast) return 'Run assessment';
    return 'Continue';
  };
  const showAI = selectedTemplate
    ? ['AI', 'Both'].includes(selectedTemplate.scope)
    : false;
  const showDatasets = selectedTemplate
    ? ['Dataset', 'Both'].includes(selectedTemplate.scope)
    : false;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={() => onCloseDialog(false)}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: 'min(780px, 92vh)',
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: 5,
          py: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 3 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: '#06362B',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              '& svg': { fontSize: 18 },
            }}
          >
            <LayersOutlinedIcon />
          </Box>
          <Typography variant="body1" sx={{ flex: 1, fontSize: 18 }}>
            Run new assessment
          </Typography>
          <IconButton
            size="small"
            aria-label="Close"
            onClick={() => onCloseDialog(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {/* alternativeLabel */}
        <Stepper activeStep={step}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ fontSize: 20 }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {loadingTemplates ? (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              p: 4,
            }}
          >
            {/* STEP 1 */}
            {step === 0 && (
              <TemplateStep
                templates={templates}
                selected={selectedTemplate ?? undefined}
                onSelect={(t) => {
                  setSelectedDataSets([]);
                  setSelectedAISystems([]);
                  setSelectedTemplate(t);
                }}
              />
            )}

            {/* STEP 2 */}
            {step === 1 && selectedTemplate && (
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="body1" className="mb-4">
                  Selected template:{' '}
                  <Box component="strong" sx={{ color: 'text.primary' }}>
                    {selectedTemplate.templateName}
                  </Box>{' '}
                  ({selectedTemplate.scope})
                </Typography>

                <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
                  {showAI && (
                    <Grid item xs={6} sx={{ minWidth: 0, display: 'flex' }}>
                      <Box
                        sx={{
                          width: '100%',
                          minWidth: 0,
                          p: { xs: 1, sm: 2 },
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                          transition: 'box-shadow 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                          },
                        }}
                      >
                        <AssessmentEntity
                          entityTitle="AI Systems"
                          defaultIds={selectedAISystems}
                          onChange={setSelectedAISystems}
                          items={aiSystems}
                          onSearch={loadAISystems}
                        />
                      </Box>
                    </Grid>
                  )}

                  {showDatasets && (
                    <Grid item xs={6} sx={{ minWidth: 0, display: 'flex' }}>
                      <Box
                        sx={{
                          width: '100%',
                          minWidth: 0,
                          p: { xs: 1, sm: 2 },
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          bgcolor: 'background.paper',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                          transition: 'box-shadow 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                          },
                        }}
                      >
                        <AssessmentEntity
                          entityTitle="Datasets"
                          defaultIds={selectedDataSets}
                          onChange={setSelectedDataSets}
                          items={datasets}
                          onSearch={loadDatasets}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {step === 2 && (
              <Box className="my-4 app-box-border px-4">
                <Typography variant="h2" className="mb-4">
                  Review & Confirm
                </Typography>

                <Typography variant="body1" className="fw-6">
                  Template
                </Typography>
                <Typography variant="body1" className="mb-4">
                  {selectedTemplate?.templateName}
                </Typography>

                <Typography variant="body1" className="fw-6">
                  Selected Entities
                </Typography>
                {selectedAISystems.length > 0 && (
                  <Typography variant="body1" className="">
                    {selectedAISystems.length} AI System
                  </Typography>
                )}
                {selectedDataSets.length > 0 && (
                  <Typography variant="body1" className="mb-4">
                    {selectedDataSets.length} Dataset
                  </Typography>
                )}
                <Typography variant="body1" className="fw-6">
                  Total Assessments to Create
                </Typography>
                <Typography variant="body1" className="mb-4 f18">
                  <b>{selectedAISystems.length + selectedDataSets.length}</b>
                </Typography>
              </Box>
            )}
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            padding={4}
          >
            <Typography variant="body1">
              {step === 0 &&
                (selectedTemplate ? (
                  <>
                    <Box component="strong">
                      {selectedTemplate.templateName}
                    </Box>{' '}
                    selected
                  </>
                ) : (
                  'No template selected'
                ))}
            </Typography>

            <Stack direction="row" spacing={2}>
              {step > 0 && (
                <AppPrimaryButtonOutlined
                  title="Back"
                  onClick={actionOnClickPrevious}
                  disabled={creatingAssessment}
                />
              )}
              <AppPrimaryButton
                title={getNextLabel()}
                disabled={!canMoveNext || creatingAssessment}
                onClick={actionOnClickNext}
                isLoading={creatingAssessment}
                endIcon={<ChevronRightIcon />}
              />
            </Stack>
          </Stack>
        </>
      )}
    </Dialog>
  );
};

export default RunRiskAssessment;
